import {
  initButtonLoggingControls,
  isButtonLoggingEnabled,
  recordButtonLoggingPreference,
} from './debugButtonLogging';

const MAX_TEXT_LENGTH = 160;

type MaybeString = string | null | undefined;

export interface LastInteraction {
  type: string;
  summary: string;
  text?: string;
  label?: string;
  tag?: string;
  id?: string;
  className?: string;
  role?: string;
  timestamp: string;
  page?: string;
  url?: string;
}

let lastInteraction: LastInteraction | null = null;
let installed = false;

type ButtonInteractionOrigin = 'pointer' | 'keyboard' | 'programmatic';

interface ButtonInteractionLog {
  id: number;
  summary: string;
  button: Element;
  startedAt: number;
  origin: ButtonInteractionOrigin;
  groupOpen: boolean;
  clickLogged: boolean;
  pointerId?: number;
  cleanupTimer?: ReturnType<typeof setTimeout>;
}

type IdleDeadlineLike = { didTimeout: boolean; timeRemaining: () => number };
type RequestIdleCallbackLike = (callback: (deadline: IdleDeadlineLike) => void, options?: { timeout?: number }) => number;

let interactionCounter = 0;
const activeInteractionsByElement = new WeakMap<Element, ButtonInteractionLog>();
const pointerInteractions = new Map<number, ButtonInteractionLog>();

function normalize(str: MaybeString): string {
  if (typeof str !== 'string') return '';
  return str.replace(/\s+/g, ' ').trim();
}

function truncate(str: string, max = MAX_TEXT_LENGTH): string {
  if (str.length <= max) return str;
  return `${str.slice(0, max - 1)}…`;
}

function describeElement(target: EventTarget | null): {
  text?: string;
  label?: string;
  tag?: string;
  id?: string;
  className?: string;
  role?: string;
} {
  if (!target || !(target instanceof Element)) {
    return {};
  }
  const tag = target.tagName ? target.tagName.toLowerCase() : undefined;
  const ariaLabel = normalize(target.getAttribute('aria-label'));
  const dataLabel = normalize(target.getAttribute('data-label') ?? target.getAttribute('data-action'));
  const textContent = normalize(target.textContent || target.getAttribute('value'));
  const label = truncate([ariaLabel, dataLabel, textContent].find(part => part) || '');
  const text = truncate(textContent);
  const id = normalize(target.id);
  const className = normalize(target.className);
  const role = normalize(target.getAttribute('role'));
  return {
    text: text || undefined,
    label: label || undefined,
    tag,
    id: id || undefined,
    className: className || undefined,
    role: role || undefined,
  };
}

function buildSummary(desc: ReturnType<typeof describeElement>): string {
  if (!desc) return 'Clicked element';
  const label = desc.label || desc.text;
  if (label) {
    return `Clicked "${label}"`;
  }
  if (desc.tag) {
    return `Clicked <${desc.tag}> element`;
  }
  return 'Clicked element';
}

function isButtonElement(target: EventTarget | null): Element | null {
  if (!target || !(target instanceof Element)) {
    return null;
  }
  const button = target.closest('button');
  if (button) return button;
  const roleButton = target.closest('[role="button"]');
  if (roleButton) return roleButton;
  return null;
}

function toPlainDataset(element: Element): Record<string, string> | undefined {
  if (!(element as HTMLElement).dataset) return undefined;
  const entries = Object.entries((element as HTMLElement).dataset).filter(([, value]) => value != null);
  if (entries.length === 0) return undefined;
  // Cast is safe: we filtered out null/undefined values.
  return Object.fromEntries(entries) as Record<string, string>;
}

function getTimestamp(): string {
  return new Date().toISOString();
}

function getNow(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function formatDelta(start: number): string {
  const delta = getNow() - start;
  return `${delta.toFixed(2)}ms`;
}

function ensureGroup(interaction: ButtonInteractionLog) {
  if (interaction.groupOpen) return;
  if (typeof console === 'undefined' || !console.groupCollapsed) return;
  try {
    console.groupCollapsed(`🔘 [#${interaction.id}] ${interaction.summary}`);
    interaction.groupOpen = true;
  } catch {
    interaction.groupOpen = false;
  }
}

function clearInteraction(interaction: ButtonInteractionLog) {
  if (interaction.cleanupTimer) {
    clearTimeout(interaction.cleanupTimer);
    interaction.cleanupTimer = undefined;
  }
  if (interaction.groupOpen && typeof console !== 'undefined' && console.groupEnd) {
    try {
      console.groupEnd();
    } catch {
      // ignore
    }
  }
  activeInteractionsByElement.delete(interaction.button);
  if (typeof interaction.pointerId === 'number') {
    pointerInteractions.delete(interaction.pointerId);
  }
}

function logToConsole(label: string, payload: Record<string, unknown>) {
  if (typeof console === 'undefined' || !console.log) return;
  try {
    console.log(label, payload);
  } catch {
    // ignore
  }
}

function createButtonInteraction(
  button: Element,
  summary: string,
  origin: ButtonInteractionOrigin,
  initialEvent: Event,
  pointerId?: number,
): ButtonInteractionLog {
  const interaction: ButtonInteractionLog = {
    id: ++interactionCounter,
    summary,
    button,
    startedAt: getNow(),
    origin,
    groupOpen: false,
    clickLogged: false,
    pointerId,
  };
  ensureGroup(interaction);

  const elementDescription = describeElement(button);
  const dataset = toPlainDataset(button);
  const details: Record<string, unknown> = {
    summary,
    origin,
    timestamp: getTimestamp(),
    tag: elementDescription.tag,
    label: elementDescription.label,
    text: elementDescription.text,
    id: elementDescription.id,
    className: elementDescription.className,
    role: elementDescription.role,
    dataset,
    pointerId,
    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  };

  logToConsole('▶️ Button interaction started', details);
  logToConsole('Initial event', { type: initialEvent.type, event: initialEvent });

  activeInteractionsByElement.set(button, interaction);
  if (typeof pointerId === 'number') {
    pointerInteractions.set(pointerId, interaction);
  }

  return interaction;
}

function withButtonInteraction(
  target: Element,
  summary: string,
  origin: ButtonInteractionOrigin,
  initialEvent: Event,
  pointerId?: number,
): ButtonInteractionLog {
  const existing = activeInteractionsByElement.get(target);
  if (existing) {
    ensureGroup(existing);
    return existing;
  }
  return createButtonInteraction(target, summary, origin, initialEvent, pointerId);
}

function scheduleAutoCleanup(interaction: ButtonInteractionLog, reason: string) {
  if (interaction.cleanupTimer) return;
  interaction.cleanupTimer = setTimeout(() => {
    logToConsole('⚠️ Button interaction auto-closed', {
      id: interaction.id,
      summary: interaction.summary,
      reason,
      elapsed: formatDelta(interaction.startedAt),
    });
    clearInteraction(interaction);
  }, 1000);
}

function cancelAutoCleanup(interaction: ButtonInteractionLog) {
  if (!interaction.cleanupTimer) return;
  clearTimeout(interaction.cleanupTimer);
  interaction.cleanupTimer = undefined;
}

function logInteractionEvent(
  interaction: ButtonInteractionLog,
  label: string,
  event: Event,
  extra: Record<string, unknown> = {},
) {
  ensureGroup(interaction);
  const payload: Record<string, unknown> = {
    id: interaction.id,
    summary: interaction.summary,
    origin: interaction.origin,
    eventType: event.type,
    sinceStart: formatDelta(interaction.startedAt),
    timestamp: getTimestamp(),
    ...extra,
  };
  logToConsole(label, payload);
  if (typeof console !== 'undefined' && console.log) {
    try {
      console.log(`${label} event`, event);
    } catch {
      // ignore
    }
  }
}

function scheduleLifecycleLogs(interaction: ButtonInteractionLog) {
  const clickStart = getNow();
  if (typeof console !== 'undefined' && console.log) {
    try {
      console.log('⏱️ Click handling started', {
        id: interaction.id,
        summary: interaction.summary,
        timestamp: getTimestamp(),
      });
    } catch {
      // ignore
    }
  }

  if (typeof queueMicrotask === 'function') {
    try {
      queueMicrotask(() => {
        logToConsole('⏳ Microtask queue drained after click', {
          id: interaction.id,
          summary: interaction.summary,
          elapsedSinceClick: `${(getNow() - clickStart).toFixed(2)}ms`,
        });
      });
    } catch {
      // ignore
    }
  }

  if (typeof window !== 'undefined') {
    if (typeof window.requestAnimationFrame === 'function') {
      try {
        window.requestAnimationFrame(() => {
          logToConsole('🎞️ Next animation frame after click', {
            id: interaction.id,
            summary: interaction.summary,
            elapsedSinceClick: `${(getNow() - clickStart).toFixed(2)}ms`,
          });
        });
      } catch {
        // ignore
      }
    }

    const requestIdleCallback = (window as Window & { requestIdleCallback?: RequestIdleCallbackLike }).requestIdleCallback;
    if (typeof requestIdleCallback === 'function') {
      try {
        requestIdleCallback((deadline) => {
          logToConsole('🛋️ Idle callback after click', {
            id: interaction.id,
            summary: interaction.summary,
            elapsedSinceClick: `${(getNow() - clickStart).toFixed(2)}ms`,
            didTimeout: deadline.didTimeout,
            timeRemaining: deadline.timeRemaining(),
          });
        });
      } catch {
        // ignore
      }
    }
  }

  setTimeout(() => {
    logToConsole('✅ Button interaction completed', {
      id: interaction.id,
      summary: interaction.summary,
      totalElapsed: formatDelta(interaction.startedAt),
      clickDuration: `${(getNow() - clickStart).toFixed(2)}ms`,
    });
    clearInteraction(interaction);
  }, 0);
}

function describeAndSummarize(button: Element) {
  const description = describeElement(button);
  const summary = buildSummary(description);
  return { description, summary };
}

function handleButtonLogging(
  event: MouseEvent,
  target: Element,
  summary: string,
  description: ReturnType<typeof describeElement>,
) {
  if (!isButtonLoggingEnabled()) return;
  const interaction = withButtonInteraction(target, summary, 'programmatic', event);
  cancelAutoCleanup(interaction);
  if (interaction.clickLogged) {
    logInteractionEvent(interaction, 'ℹ️ Additional click event observed', event, {
      detail: event.detail,
      button: event.button,
      buttons: event.buttons,
    });
    return;
  }

  interaction.clickLogged = true;
  logInteractionEvent(interaction, '🖱️ Click event captured', event, {
    detail: event.detail,
    button: event.button,
    buttons: event.buttons,
    clientX: event.clientX,
    clientY: event.clientY,
  });

  logToConsole('Button state snapshot', {
    id: interaction.id,
    summary,
    disabled:
      target instanceof HTMLButtonElement
        ? target.disabled
        : target.getAttribute('aria-disabled') === 'true',
    type:
      target instanceof HTMLButtonElement
        ? target.type
        : target.getAttribute('type') || undefined,
    role: description.role,
    label: description.label,
    text: description.text,
  });

  if (typeof console !== 'undefined' && console.log) {
    try {
      console.log('DOM element', target);
    } catch {
      // ignore
    }
  }

  scheduleLifecycleLogs(interaction);
}

function handlePointerDown(event: PointerEvent) {
  if (!isButtonLoggingEnabled()) return;
  const button = isButtonElement(event.target);
  if (!button) return;
  const { summary } = describeAndSummarize(button);
  const interaction = withButtonInteraction(button, summary, 'pointer', event, event.pointerId);
  cancelAutoCleanup(interaction);
  logInteractionEvent(interaction, '👇 Pointer down on button', event, {
    pointerType: event.pointerType,
    buttons: event.buttons,
    pressure: event.pressure,
    isPrimary: event.isPrimary,
  });
}

function handlePointerUp(event: PointerEvent) {
  if (!isButtonLoggingEnabled()) return;
  const existing = pointerInteractions.get(event.pointerId);
  const button = existing?.button ?? isButtonElement(event.target);
  if (!button) return;
  const { summary } = describeAndSummarize(button);
  const interaction = existing || withButtonInteraction(button, summary, 'pointer', event, event.pointerId);
  cancelAutoCleanup(interaction);
  logInteractionEvent(interaction, '☝️ Pointer up on button', event, {
    pointerType: event.pointerType,
    buttons: event.buttons,
    isPrimary: event.isPrimary,
  });
  scheduleAutoCleanup(interaction, 'pointerup without click');
}

function handlePointerCancel(event: PointerEvent) {
  if (!isButtonLoggingEnabled()) return;
  const interaction = pointerInteractions.get(event.pointerId);
  if (!interaction) return;
  logInteractionEvent(interaction, '⛔ Pointer cancelled on button', event, {
    pointerType: event.pointerType,
    isPrimary: event.isPrimary,
  });
  clearInteraction(interaction);
}

function isButtonKeyboardActivation(event: KeyboardEvent): boolean {
  return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
}

function handleKeyDown(event: KeyboardEvent) {
  if (!isButtonLoggingEnabled()) return;
  if (!isButtonKeyboardActivation(event)) return;
  const button = isButtonElement(event.target);
  if (!button) return;
  const { summary } = describeAndSummarize(button);
  const interaction = withButtonInteraction(button, summary, 'keyboard', event);
  cancelAutoCleanup(interaction);
  logInteractionEvent(interaction, '⌨️ Key down on button', event, {
    key: event.key,
    code: event.code,
    repeat: event.repeat,
  });
}

function handleKeyUp(event: KeyboardEvent) {
  if (!isButtonLoggingEnabled()) return;
  if (!isButtonKeyboardActivation(event)) return;
  const button = isButtonElement(event.target);
  if (!button) return;
  const { summary } = describeAndSummarize(button);
  const interaction = withButtonInteraction(button, summary, 'keyboard', event);
  logInteractionEvent(interaction, '↩️ Key up on button', event, {
    key: event.key,
    code: event.code,
  });
  scheduleAutoCleanup(interaction, 'keyboard activation without click');
}

function handleClick(event: MouseEvent) {
  if (typeof window === 'undefined') return;
  const details = describeElement(event.target);
  const summary = buildSummary(details);
  const interaction: LastInteraction = {
    type: 'click',
    summary,
    text: details.text,
    label: details.label,
    tag: details.tag,
    id: details.id,
    className: details.className,
    role: details.role,
    timestamp: new Date().toISOString(),
    page: window.location.pathname,
    url: window.location.href,
  };
  lastInteraction = interaction;

  const buttonTarget = isButtonElement(event.target);
  if (buttonTarget) {
    const { description, summary: buttonSummary } = describeAndSummarize(buttonTarget);
    handleButtonLogging(event, buttonTarget, buttonSummary, description);
  }
}

export function initInteractionTracker() {
  if (installed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;
  recordButtonLoggingPreference();
  initButtonLoggingControls();
  document.addEventListener('pointerdown', handlePointerDown, { capture: true, passive: true });
  document.addEventListener('pointerup', handlePointerUp, { capture: true, passive: true });
  document.addEventListener('pointercancel', handlePointerCancel, { capture: true, passive: true });
  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('keyup', handleKeyUp, true);
  document.addEventListener('click', handleClick, { capture: true });
}

export function getLastInteraction(): LastInteraction | null {
  return lastInteraction;
}
